"use client";

import { useState, useMemo } from "react";
import { EventFilters } from "./event-filters";
import { EventCalendarView } from "./event-calendar-view";
import { EventListView } from "./event-list-view";
import type { RowingEvent } from "@/types";

interface EventsClientProps {
  events: RowingEvent[];
  countries: string[];
  boatClasses: string[];
  eventTypes: string[];
}

export function EventsClient({
  events,
  countries,
  boatClasses,
  eventTypes,
}: EventsClientProps) {
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedBoatClasses, setSelectedBoatClasses] = useState<string[]>([]);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);

  const months = Array.from(new Set(events.map((e) => e.month))).sort(
    (a, b) => a - b
  );

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (selectedMonth !== null && event.month !== selectedMonth) return false;
      if (selectedCountry !== null && event.location.country !== selectedCountry)
        return false;
      if (selectedEventType !== null && event.eventType !== selectedEventType)
        return false;
      if (
        selectedBoatClasses.length > 0 &&
        !selectedBoatClasses.some((bc) => event.boatClasses.includes(bc))
      )
        return false;
      return true;
    });
  }, [events, selectedMonth, selectedCountry, selectedBoatClasses, selectedEventType]);

  function handleBoatClassToggle(bc: string) {
    setSelectedBoatClasses((prev) =>
      prev.includes(bc) ? prev.filter((c) => c !== bc) : [...prev, bc]
    );
  }

  function handleClear() {
    setSelectedMonth(null);
    setSelectedCountry(null);
    setSelectedBoatClasses([]);
    setSelectedEventType(null);
  }

  return (
    <div className="space-y-6">
      {/* View toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setView("calendar")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            view === "calendar"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Calendar
        </button>
        <button
          onClick={() => setView("list")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            view === "list"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          List
        </button>
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
          {filteredEvents.length} {filteredEvents.length === 1 ? "event" : "events"}
        </span>
      </div>

      {/* Filters */}
      <EventFilters
        months={months}
        countries={countries}
        boatClasses={boatClasses}
        eventTypes={eventTypes}
        selectedMonth={selectedMonth}
        selectedCountry={selectedCountry}
        selectedBoatClasses={selectedBoatClasses}
        selectedEventType={selectedEventType}
        onMonthChange={setSelectedMonth}
        onCountryChange={setSelectedCountry}
        onBoatClassToggle={handleBoatClassToggle}
        onEventTypeChange={setSelectedEventType}
        onClear={handleClear}
      />

      {/* View */}
      {view === "calendar" ? (
        <EventCalendarView events={filteredEvents} />
      ) : (
        <EventListView events={filteredEvents} />
      )}
    </div>
  );
}
